import { action, computed, observable } from "mobx";

import {
  ClinicalDataType,
  DockableClinicalTab,
  EncounterType,
  PatientTab
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { UserStorageKeys } from "@libs/gateways/user-experience/UserExperienceGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";

import { ClinicalTabs } from "./models/clinical-tab/ClinicalTabs.ts";
import { ClinicalRecord } from "./models/ClinicalRecord.ts";

export class ClinicalUi {
  constructor(private root: IRootStore) {}

  get clinical() {
    return this.root.clinical;
  }

  get core() {
    return this.root.core;
  }

  get routing() {
    return this.root.routing;
  }

  @observable closingClinicalRecordId: string | undefined;
  @observable isConfirmationDialogVisible = false;
  @observable isConditionConsultsExportDialogVisible: boolean = false;
  @observable isUnlinkConditionConfirmationDialogVisible: boolean = false;
  @observable conditionConsultsDocumentId: string | undefined = undefined;
  public clinicalTabs: ClinicalTabs;
  openedRecordIds = observable.array<PatientTab>();

  get tabs(): ClinicalTabs {
    if (!this.clinicalTabs) this.clinicalTabs = new ClinicalTabs(this.root);
    return this.clinicalTabs;
  }

  @action
  closePatientRecordContentForm(
    clinicalRecord: ClinicalRecord,
    type: DockableClinicalTab,
    contextId?: string
  ) {
    const patientRecordTab = this.tabs.getPatientRecordTab(clinicalRecord);

    const tab = patientRecordTab?.allTabs.find(
      x => x.type === type && x.contextId === contextId
    );

    if (tab) {
      patientRecordTab?.removeTab(
        tab.id,
        patientRecordTab?.activeTab?.id === tab.id
      );
    } else {
      patientRecordTab?.hideActive();
    }
  }

  @action
  setPatientClinicalContent(data: {
    clinicalRecord: ClinicalRecord;
    type: DockableClinicalTab;
    encounterId?: string;
    title?: string;
    contextId?: string;
    toolTipText?: string;
    defaultPivot?: string;
  }) {
    const { clinicalRecord, ...dataWithoutClinicalRecord } = data;
    const patientRecordTab = this.tabs.getPatientRecordTab(clinicalRecord);

    // a case when we switch bte opened tabs in docker
    if (
      patientRecordTab.hasTabTypeOpen(
        data.type as ClinicalDataType,
        data.encounterId,
        data.contextId
      )
    ) {
      patientRecordTab.setActive(
        data.type as ClinicalDataType,
        data.encounterId,
        data.contextId
      );
      return;
    }

    // if no we create a new tab and show one
    const tab = this.tabs.createClinicalTabItem(dataWithoutClinicalRecord);
    patientRecordTab.show(tab);
  }

  @action
  changeRecordPosition(recordId: string, encounterId?: string) {
    const record = encounterId
      ? this.openedRecordIds.find(
          x => x.patientId === recordId && x.encounterId === encounterId
        )
      : this.openedRecordIds.find(
          x => x.patientId === recordId && !x.encounterId
        );

    if (record) {
      const index = this.openedRecordIds.indexOf(record);
      if (index > -1) {
        this.openedRecordIds.splice(index, 1);
      }

      if (encounterId) {
        this.setActiveRecord({
          patientId: record.patientId,
          encounterId: record.encounterId
        });
      } else {
        this.clinical.setActiveViewOnlyRecord(recordId);
      }

      this.openedRecordIds.splice(0, 0, record);
    }
  }

  @action resetClosingClinicalRecordId = () => {
    this.closingClinicalRecordId = undefined;
  };

  @action closeDiscardConfirmationDialog = () => {
    this.isConfirmationDialogVisible = false;
  };

  @action setConditionConsultsExportDialogVisible = (value: boolean) => {
    this.isConditionConsultsExportDialogVisible = value;
  };

  @action setIsUnlinkConditionConfirmationDialogVisible = (value: boolean) => {
    this.isUnlinkConditionConfirmationDialogVisible = value;
  };

  @action setConditionConsultsDocumentId = (value: string | undefined) => {
    this.conditionConsultsDocumentId = value;
  };

  @computed
  get clinicalTaskActionDisabled() {
    return (
      this.core.hasPermissions(Permission.LicencingAllowed) &&
      !this.core.hasPermissions(Permission.CreateClinicalTasksAllowed)
    );
  }

  @computed
  get clinicalActivityActionDisabled() {
    return (
      (this.core.hasPermissions(Permission.LicencingAllowed) &&
        !this.core.hasPermissions(Permission.CreateClinActivityAllowed)) ||
      !this.core.hasPermissions(Permission.ClinActivityWrite)
    );
  }

  /**
   * Navigates Omni to a particular clinical record, or navigates away from the existing record
   */
  setActiveRecord(
    data:
      | {
          patientId: string;
          encounterId: string | undefined;
        }
      | undefined
  ) {
    if (data && data.patientId) {
      const id = data.patientId;
      const encounterId = data.encounterId;
      const clinicalRecord = this.clinical.getEncounterClinicalRecord(
        id,
        encounterId
      );
      if (!encounterId) {
        this.routing.push({
          pathname: routes.records.recordView.path({ id })
        });
      } else if (
        !clinicalRecord ||
        clinicalRecord.openEncounter?.type === EncounterType.Consultation ||
        clinicalRecord.openEncounter?.type ===
          EncounterType.PhoneConsultation ||
        clinicalRecord.openEncounter?.type ===
          EncounterType.VideoConsultation ||
        !clinicalRecord.openEncounter
      ) {
        this.routing.push({
          pathname: routes.records.encounter.path({ id, encounterId })
        });
      } else if (
        clinicalRecord.openEncounter?.type === EncounterType.RecordUpdate
      ) {
        this.routing.push({
          pathname: routes.records.recordUpdate.path({ id })
        });
      }
    } else {
      this.routing.goToFromState(routes.calendarEvents.basePath.pattern);
    }
  }

  @action
  updateOpenedRecordIds(patientId: string, encounterId: string | undefined) {
    const tabAlready = this.openedRecordIds.some(
      x => x.patientId === patientId && x.encounterId === encounterId
    );
    if (!tabAlready) {
      this.openedRecordIds.splice(0, 0, {
        patientId,
        encounterId
      });

      this.clinical.saveToUserStorage(
        UserStorageKeys.OpenPatientTabs,
        this.openedRecordIds
      );
    }
  }

  /**
   * This property should not be used on the clinical record screen.
   * It should only be used sparingly and only for parts of Omni outside of the clinical record screen (such as the QuickLinks)
   */
  @observable
  activeClinicalRecord: ClinicalRecord | undefined;
}
